import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { useTranslate } from '@tolgee/react';
import { useSelector } from 'react-redux';
import { dispatch } from '../../../redux/store';
import {
  updatePostBoostConfig,
  updatePlatformEnabled,
  fetchMetaAdsInterests,
  fetchMetaAdsCountries,
  fetchMetaAdsCities
} from '../../../redux/slices/lomavisCreator/lomavisCreator';
import { LomavisTypeaheadSelectInput1 } from '../../../lomavisSharedUi/atoms/inputs/LomavisTypeaheadSelectInput1';
import { LomavisPlatformIcon } from '../../../lomavisSharedUi/atoms/icons/LomavisPlatformIcon';
import toast from 'react-hot-toast';
import { debounce } from 'lodash';
import { FACEBOOK, INSTAGRAM } from '../../../config/constants';

// Types
interface BoostConfig {
  active?: boolean;
  goal?: any;
  beneficiary?: string;
  budget?: number;
  days?: number;
  special_requirements?: string;
  interests?: any[];
  countries?: any[];
  cities?: any[];
  gender?: any[];
  min_age?: number;
  max_age?: number;
}

interface Option {
  id: string;
  name: string;
}

interface PostBoostCardProps {
  platforms: string[];
  platformConfigs: { [platform: string]: BoostConfig };
}

// Constants
const ARRAY_FIELDS = ['interests', 'countries', 'cities', 'gender'] as const;
const CONFIG_FIELDS = [
  'active', 'goal', 'beneficiary', 'budget', 'days', 'special_requirements',
  'interests', 'countries', 'cities', 'gender', 'min_age', 'max_age'
] as const;

const AGE_LIMITS = {
  MIN: 13,
  MAX: 65
} as const;

const PLATFORM_NAMES = {
  [FACEBOOK]: 'Facebook',
  [INSTAGRAM]: 'Instagram'
};

export const PostBoostCard: React.FC<PostBoostCardProps> = ({ platforms, platformConfigs }) => {
  // ============================================================================
  // HOOKS & STATE
  // ============================================================================
  const { t } = useTranslate();
  const { platformEnabled } = useSelector((state: any) => state.lomavisCreator);
  
  // Component state
  const [interestsOptions, setInterestsOptions] = useState<Option[]>([]);
  const [countriesOptions, setCountriesOptions] = useState<Option[]>([]);
  const [citiesOptions, setCitiesOptions] = useState<Option[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Check platform statuses
  const areAllPlatformsEnabled = platforms.every(platform => platformEnabled[platform]);
  const areAnyPlatformsEnabled = platforms.some(platform => platformEnabled[platform]);
  
  // Check if any platform has active boost
  const isAnyBoostActive = platforms.some(platform => {
    const config = platformConfigs[platform];
    // Default to active (true) when active flag is null or undefined
    return config?.active !== false;
  });
  const areAllBoostsActive = platforms.every(platform => {
    const config = platformConfigs[platform];
    // Default to active (true) when active flag is null or undefined
    return config?.active !== false;
  });

  // For multi-platform cards, set boost active by default
  const isMultiPlatformCard = platforms.length > 1;
  
  // Get the first platform's config for form display (we'll sync all platforms)
  const primaryPlatform = platforms[0];
  const primaryPlatformConfig = platformConfigs[primaryPlatform] || {};

  // Initialize boost configs for multi-platform cards
  useEffect(() => {
    if (isMultiPlatformCard && !isAnyBoostActive) {
      platforms.forEach(platform => {
        dispatch(updatePostBoostConfig({ platform, attribute: 'active', value: true }));
      });
    }
  }, [isMultiPlatformCard, isAnyBoostActive, platforms, dispatch]);

  // ============================================================================
  // CONFIGURATION & OPTIONS
  // ============================================================================
  const getStaticOptions = () => ({
    goal: [
      { id: 'OUTCOME_AWARENESS', name: t('lomavisCreator.postBoost.goalAwareness') },
      { id: 'OUTCOME_ENGAGEMENT', name: t('lomavisCreator.postBoost.goalEngagement') },
      { id: 'OUTCOME_LEADS', name: t('lomavisCreator.postBoost.goalLeads') },
      { id: 'OUTCOME_SALES', name: t('lomavisCreator.postBoost.goalSales') },
      { id: 'OUTCOME_TRAFFIC', name: t('lomavisCreator.postBoost.goalTraffic') },
      { id: 'OUTCOME_APP_PROMOTION', name: t('lomavisCreator.postBoost.goalAppPromotion') }
    ],
    specialRequirements: [
      { id: 'HOUSING', name: t('lomavisCreator.postBoost.specialRequirementsHousing') },
      { id: 'FINANCIAL_PRODUCTS_SERVICES', name: t('lomavisCreator.postBoost.specialRequirementsFinancial') },
      { id: 'EMPLOYMENT', name: t('lomavisCreator.postBoost.specialRequirementsEmployment') },
      { id: 'ISSUES_ELECTIONS_POLITICS', name: t('lomavisCreator.postBoost.specialRequirementsPolitics') },
      // { id: 'NONE', name: t('lomavisCreator.postBoost.specialRequirementsNone') }
    ],
    gender: [
      { id: '1', name: t('lomavisCreator.postBoost.genderMale') },
      { id: '2', name: t('lomavisCreator.postBoost.genderFemale') },
      { id: 'all', name: t('lomavisCreator.postBoost.genderAll') }
    ]
  });

  // ============================================================================
  // API FETCH FUNCTIONS
  // ============================================================================
  // API fetch functions
  const fetchInterestsOptions = useCallback(
    debounce(async (query?: string, setLoading?: (loading: boolean) => void) => {
      if (!query) {
        setInterestsOptions([]);
        return [];
      }
      
      setLoading?.(true);
      try {
        const result = await dispatch(fetchMetaAdsInterests(query));
        if (result?.data) {
          setInterestsOptions(result.data);
          return result.data;
        }
        return [];
      } catch (error) {
        console.error('Error fetching interests:', error);
        setInterestsOptions([]);
        return [];
      } finally {
        setLoading?.(false);
      }
    }, 200),
    []
  );

  const fetchCountriesOptions = useCallback(
    debounce(async (query?: string, setLoading?: (loading: boolean) => void) => {
      if (!query) {
        setCountriesOptions([]);
        return [];
      }
      
      setLoading?.(true);
      try {
        const result = await dispatch(fetchMetaAdsCountries(query));
        if (result?.data) {
          setCountriesOptions(result.data);
          return result.data;
        }
        return [];
      } catch (error) {
        console.error('Error fetching countries:', error);
        setCountriesOptions([]);
        return [];
      } finally {
        setLoading?.(false);
      }
    }, 200),
    []
  );

  const fetchCitiesOptions = useCallback(
    debounce(async (query?: string, setLoading?: (loading: boolean) => void) => {
      if (!query) {
        setCitiesOptions([]);
        return [];
      }
      
      setLoading?.(true);
      try {
        const result = await dispatch(fetchMetaAdsCities(query));
        if (result?.data) {
          setCitiesOptions(result.data);
          return result.data;
        }
        return [];
      } catch (error) {
        console.error('Error fetching cities:', error);
        setCitiesOptions([]);
        return [];
      } finally {
        setLoading?.(false);
      }
    }, 200),
    []
  );

  // ============================================================================
  // CONFIGURATION HANDLERS
  // ============================================================================
  const handleConfigChange = (field: string, values: any[]) => {
    const isArrayField = ARRAY_FIELDS.includes(field as any);
    const value = isArrayField ? values : (values[0] || null);
    
    // Update all platforms
    platforms.forEach(platform => {
      dispatch(updatePostBoostConfig({ platform, attribute: field, value }));
    });
  };

  const handleNumberChange = (field: string, value: number | null) => {
    // Update all platforms
    platforms.forEach(platform => {
      dispatch(updatePostBoostConfig({ platform, attribute: field, value }));
    });
  };

  const handleStringChange = (field: string, value: string | null) => {
    // Update all platforms
    platforms.forEach(platform => {
      dispatch(updatePostBoostConfig({ platform, attribute: field, value }));
    });
  };

  // ============================================================================
  // VALIDATION LOGIC
  // ============================================================================
  const validateRequiredFields = useCallback((): string[] => {
    const errors: string[] = [];
    const validations = [
      { field: primaryPlatformConfig.goal, message: t('lomavisCreator.postBoost.validation.goalRequired') },
      { 
        field: primaryPlatformConfig.beneficiary?.trim(), 
        message: t('lomavisCreator.postBoost.validation.beneficiaryRequired') 
      },
      { 
        field: primaryPlatformConfig.budget && primaryPlatformConfig.budget > 0, 
        message: t('lomavisCreator.postBoost.validation.budgetRequired') 
      },
      { 
        field: primaryPlatformConfig.days && primaryPlatformConfig.days > 0, 
        message: t('lomavisCreator.postBoost.validation.daysRequired') 
      }
    ];

    validations.forEach(({ field, message }) => {
      if (!field) errors.push(message);
    });

    return errors;
  }, [primaryPlatformConfig, t]);

  const validateAgeFields = useCallback((): string[] => {
    const errors: string[] = [];
    const { min_age, max_age } = primaryPlatformConfig;

    if (min_age && (min_age < AGE_LIMITS.MIN || min_age > AGE_LIMITS.MAX)) {
      errors.push(t('lomavisCreator.postBoost.validation.minAgeRange'));
    }
    
    if (max_age && (max_age < AGE_LIMITS.MIN || max_age > AGE_LIMITS.MAX)) {
      errors.push(t('lomavisCreator.postBoost.validation.maxAgeRange'));
    }
    
    if (min_age && max_age && min_age >= max_age) {
      errors.push(t('lomavisCreator.postBoost.validation.ageRangeInvalid'));
    }
    
    return errors;
  }, [primaryPlatformConfig, t]);


  // Get platform display names
  const platformDisplayNames = platforms.map(platform => PLATFORM_NAMES[platform] || platform).join(', ');

  // ============================================================================
  // ACTION HANDLERS
  // ============================================================================
  const handleToggleBoostActive = useCallback(() => {
    const newActiveState = !isAnyBoostActive;
    
    // Update all platforms
    platforms.forEach(platform => {
      dispatch(updatePostBoostConfig({ 
        platform, 
        attribute: 'active', 
        value: newActiveState 
      }));
    });
    
    // Show success message
    const message = newActiveState 
      ? t('lomavisCreator.postBoost.boostActivated', { platform: platformDisplayNames })
      : t('lomavisCreator.postBoost.boostDeactivated', { platform: platformDisplayNames });
    
    toast.success(message, {
      duration: 2000,
      position: 'top-right'
    });
  }, [platforms, isAnyBoostActive, platformDisplayNames, t]);

  // Memoized static options to avoid recreation on each render
  const staticOptions = useMemo(() => getStaticOptions(), [t]);

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <div className="card mb-4 shadow-sm">
      {/* Platform Header with toggle */}
      <div className="card-header d-flex justify-content-between align-items-center py-3 px-4">
        <div className="d-flex align-items-center gap-3">
          {platforms.map(platform => (
            <LomavisPlatformIcon key={platform} platform={platform} />
          ))}
        </div>
        
        {/* Platform Toggle Switch - Only show for single platform cards */}
        {!isMultiPlatformCard && (
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id={`platforms-toggle-${platforms.join('-')}`}
              checked={isAnyBoostActive}
              onChange={handleToggleBoostActive}
              disabled={!areAnyPlatformsEnabled}
            />
            <label className="form-check-label ms-2" htmlFor={`platforms-toggle-${platforms.join('-')}`}>
              {isAnyBoostActive ? t('lomavisCreator.postBoost.boostActive') : t('lomavisCreator.postBoost.boostInactive')}
            </label>
          </div>
        )}
      </div>
      
      {/* Only show card body when boost is active (for single platform cards) or platform is enabled */}
      {(isMultiPlatformCard || isAnyBoostActive) && (
        <div className={`card-body p-4 ${(!areAnyPlatformsEnabled || (!isMultiPlatformCard && !isAnyBoostActive)) ? 'opacity-50' : ''}`}>
        {/* Platform disabled message */}
        {!areAnyPlatformsEnabled && (
          <div className="alert alert-warning mb-4">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {t('lomavisCreator.postBoost.platformNotEnabledMessage', { platform: platformDisplayNames })}
          </div>
        )}
        
        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="alert alert-danger mb-4">
            <ul className="mb-0">
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* REQUIRED FIELDS SECTION */}
        <div className="mb-4">
          {/* Helper text */}
          <p className="fw-bolder mb-3 h4">
            {t('lomavisCreator.postBoost.setup')}
          </p>
          
          {/* Goal */}
          {/*<div className="mb-3">
            <label className="form-label fw-bold">{t('lomavisCreator.postBoost.goal')} *</label>
            <select
              className="form-select"
              value={primaryPlatformConfig.goal?.id || ''}
              disabled={!areAnyPlatformsEnabled || !isAnyBoostActive}
              onChange={e => {
                const selectedOption = staticOptions.goal.find(option => option.id === e.target.value);
                handleConfigChange('goal', selectedOption ? [selectedOption] : []);
              }}
            >
              <option value="">{t('lomavisCreator.postBoost.goalPlaceholder')}</option>
              {staticOptions.goal.map(option => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>*/}

          {/* Budget, Days, and Total Budget on same row */}
          <div className="row mb-3">
            <div className="col-4">
              <label className="form-label fw-bold">{t('lomavisCreator.postBoost.budget')} *</label>
              <input
                type="number"
                className="form-control"
                step="1"
                min="1"
                value={primaryPlatformConfig.budget || ''}
                disabled={!areAnyPlatformsEnabled || !isAnyBoostActive}
                onChange={e => handleNumberChange('budget', e.target.value ? Number(e.target.value) : null)}
              />
              <small className="text-muted">{t('lomavisCreator.postBoost.budgetDescription')}</small>
            </div>
            <div className="col-4">
              <label className="form-label fw-bold">{t('lomavisCreator.postBoost.days')} *</label>
              <input
                type="number"
                className="form-control"
                min="1"
                max="30"
                value={primaryPlatformConfig.days || ''}
                disabled={!areAnyPlatformsEnabled || !isAnyBoostActive}
                onChange={e => handleNumberChange('days', e.target.value ? Number(e.target.value) : null)}
              />
              <small className="text-muted">{t('lomavisCreator.postBoost.daysDescription')}</small>
            </div>
            <div className="col-4">
              <label className="form-label fw-bold">{t('lomavisCreator.postBoost.totalBudget')}</label>
              <div className="p-2 bg-light border rounded fw-bold fs-5 d-flex align-items-center" style={{ minHeight: '38px' }}>
                {primaryPlatformConfig.budget && primaryPlatformConfig.days 
                  ? ((primaryPlatformConfig.budget * primaryPlatformConfig.days) * platforms.length).toLocaleString()
                  : '--'
                }
              </div>
              <small className="text-muted">{t('lomavisCreator.postBoost.totalBudgetDescription')}</small>
            </div>
          </div>
        </div>

          {/* Beneficiary */}
          <div className="mb-3">
            <label className="form-label fw-bold">{t('lomavisCreator.postBoost.beneficiary')} *</label>
            <input
              type="text"
              className="form-control"
              placeholder={t('lomavisCreator.postBoost.beneficiaryPlaceholder')}
              value={primaryPlatformConfig.beneficiary || ''}
              disabled={!areAnyPlatformsEnabled || !isAnyBoostActive}
              onChange={e => handleStringChange('beneficiary', e.target.value)}
            />
          </div>

        {/* Special Requirements */}
        <div className="mb-3">
          <label className="form-label fw-bold">{t('lomavisCreator.postBoost.specialRequirements')}</label>
          <select
            className="form-select"
            value={(() => {
              const specialReqs = primaryPlatformConfig.special_requirements;
              if (!specialReqs) return '';
              if (typeof specialReqs === 'string') return specialReqs;
              if (typeof specialReqs === 'object' && 'id' in specialReqs) {
                return (specialReqs as any).id || '';
              }
              return '';
            })()}
            disabled={!areAnyPlatformsEnabled || !isAnyBoostActive}
            onChange={e => {
              const selectedOption = staticOptions.specialRequirements.find(option => option.id === e.target.value);
              handleConfigChange('special_requirements', selectedOption ? [selectedOption] : []);
            }}
          >
            <option value="">{t('lomavisCreator.postBoost.specialRequirementsPlaceholder')}</option>
            {staticOptions.specialRequirements.map(option => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
          <small className="text-muted">{t('lomavisCreator.postBoost.specialRequirementsDescription')}</small>
        </div>

        {/* DIVIDER */}
        <hr className="my-4" />
        <div className="mb-4">
          {/* Helper text */}
          <p className="h4 fw-bolder mb-3">
            {t('lomavisCreator.postBoost.targeting')}
          </p>
        {/* ADDITIONAL TARGETING FIELDS */}
        <div className="mb-4">
          {/* Interests */}
          <LomavisTypeaheadSelectInput1
            getOptions={() => interestsOptions}
            onSelectionChange={(vals) => handleConfigChange('interests', vals)}
            initialValues={Array.isArray(primaryPlatformConfig.interests) ? primaryPlatformConfig.interests : []}
            title={t('lomavisCreator.postBoost.interests')}
            placeholder={t('lomavisCreator.postBoost.typeToSearch')}
            onInputChange={fetchInterestsOptions}
            minLength={1}
            disabled={!areAnyPlatformsEnabled || !isAnyBoostActive}
          />

          {/* Countries */}
          <LomavisTypeaheadSelectInput1
            getOptions={() => countriesOptions}
            onSelectionChange={(vals) => handleConfigChange('countries', vals)}
            initialValues={Array.isArray(primaryPlatformConfig.countries) ? primaryPlatformConfig.countries : []}
            title={t('lomavisCreator.postBoost.countries')}
            placeholder={t('lomavisCreator.postBoost.typeToSearch')}
            onInputChange={fetchCountriesOptions}
            minLength={1}
            disabled={!areAnyPlatformsEnabled || !isAnyBoostActive}
          />

          {/* Cities */}
          <LomavisTypeaheadSelectInput1
            getOptions={() => citiesOptions}
            onSelectionChange={(vals) => handleConfigChange('cities', vals)}
            initialValues={Array.isArray(primaryPlatformConfig.cities) ? primaryPlatformConfig.cities : []}
            title={t('lomavisCreator.postBoost.cities')}
            placeholder={t('lomavisCreator.postBoost.typeToSearch')}
            onInputChange={fetchCitiesOptions}
            minLength={1}
            disabled={!areAnyPlatformsEnabled || !isAnyBoostActive}
          />

          {/* Gender */}
          <LomavisTypeaheadSelectInput1
            getOptions={() => staticOptions.gender}
            onSelectionChange={(vals) => handleConfigChange('gender', vals)}
            initialValues={Array.isArray(primaryPlatformConfig.gender) ? primaryPlatformConfig.gender : []}
            title={t('lomavisCreator.postBoost.gender')}
            placeholder={t('lomavisCreator.postBoost.typeToSearch')}
            disabled={!areAnyPlatformsEnabled || !isAnyBoostActive}
          />

          {/* Min Age and Max Age on same line */}
          <div className="mb-3">
            <div className="row">
              <div className="col-6">
                <label className="form-label fw-bold">{t('lomavisCreator.postBoost.minAge')}</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="18"
                  min="13"
                  max="65"
                  value={primaryPlatformConfig.min_age || ''}
                  disabled={!areAnyPlatformsEnabled || !isAnyBoostActive}
                  onChange={e => handleNumberChange('min_age', e.target.value ? Number(e.target.value) : null)}
                />
                <small className="text-muted">{t('lomavisCreator.postBoost.minAgeDescription')}</small>
              </div>
              <div className="col-6">
                <label className="form-label fw-bold">{t('lomavisCreator.postBoost.maxAge')}</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="65"
                  min="13"
                  max="65"
                  value={primaryPlatformConfig.max_age || ''}
                  disabled={!areAnyPlatformsEnabled || !isAnyBoostActive}
                  onChange={e => handleNumberChange('max_age', e.target.value ? Number(e.target.value) : null)}
                />
                <small className="text-muted">{t('lomavisCreator.postBoost.maxAgeDescription')}</small>
              </div>
            </div>
          </div>
        </div>
        </div>
        </div>
        )}
        </div>
      )}
      